import React from 'react';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import { openAuthView } from '../../modules/auth';

import './legal.css';

const renderBrand = () => (
  <>
    SO<span className="legal-page__brand-accent">A</span>L
    <span className="legal-page__brand-accent">I</span>VE
  </>
);

const DOCUMENTS = {
  terms: {
    title: 'Terms of Use',
    effectiveDate: '8 May 2026',
    intro: [
      'By creating an account, accessing, or using SoAlive, you acknowledge that you have read, understood, and agree to be bound by these Terms.',
      'If you do not agree to these Terms, you must not access or use the Service.',
    ],
    sections: [
      {
        title: '1. About SoAlive',
        body: [
          'SoAlive is a web-based AI image generation platform that enables users to generate visual content using artificial intelligence models based on text prompts, uploaded reference materials, or other user instructions.',
          'SoAlive may utilise proprietary models, third-party AI models, or integrated AI technologies to provide the Service.',
        ],
      },
      {
        title: '2. Eligibility',
        body: ['By accessing or using the Service, you represent and warrant that:'],
        bullets: [
          'you have the legal capacity to accept these Terms under applicable law;',
          'your use of the Service complies with all applicable laws and regulations.',
          'if you are using the Service on behalf of a company, organisation, or other legal entity, you have authority to bind that entity to these Terms.',
        ],
      },
      {
        title: '3. Access and Use of the Service',
        body: [
          'Subject to your compliance with these Terms, SoAlive grants you a limited, non-exclusive, non-transferable, revocable right to access and use the Service for personal or internal business use.',
          'Your access to the Service does not transfer ownership of SoAlive, its software, AI models, technologies, or any associated intellectual property.',
          'We may modify, update, suspend, or discontinue any part of the Service from time to time.',
        ],
      },
      {
        title: '4. User Input and Generated Output',
        groups: [
          {
            title: '4.1 User Input',
            body: [
              'You may submit text prompts, uploaded images, reference materials, creative instructions, or other content through the Service ("Input").',
              'You retain ownership of your Input, provided that you have the lawful right to use and submit it.',
              'You are solely responsible for ensuring that your Input does not infringe any third-party rights or violate applicable law.',
            ],
          },
          {
            title: '4.2 Generated Output',
            body: [
              'Images or other content generated by the Service in response to your Input are referred to as "Output". Subject to these Terms and applicable law:',
            ],
            bullets: [
              'you may use Output generated through your account for personal or commercial purposes;',
              'you are responsible for reviewing Output before publishing, distributing, licensing, or otherwise using it;',
              'Output may not be unique, and similar or identical outputs may be generated for other users.',
            ],
            closing:
              'SoAlive does not guarantee that Output will be unique, original, or free from third-party claims.',
          },
        ],
      },
      {
        title: '5. Data Usage and AI Model Training',
        groups: [
          {
            title: '5.1 Service Operation',
            body: ['To provide the Service, SoAlive may process your Input and Output for purposes including:'],
            bullets: [
              'generating requested images;',
              'storing workspace or generation history where applicable;',
              'operating and maintaining platform functionality;',
              'security monitoring;',
              'abuse prevention;',
              'troubleshooting and service performance.',
            ],
          },
          {
            title: '5.2 No Training Without User Consent',
            body: [
              'SoAlive will not use your prompts, uploaded content, generated outputs, or other user content to train, retrain, fine-tune, or improve artificial intelligence models without your explicit prior consent.',
              'Where SoAlive wishes to use user content for AI model training, model improvement, or dataset enhancement, we will request your prior consent through an opt-in mechanism.',
              'Your decision not to provide consent will not affect your access to or use of the Service.',
            ],
            bullets: [
              'text prompts submitted by you;',
              'uploaded images or reference files;',
              'generated images or outputs created through your use of the Service.',
            ],
          },
          {
            title: '5.3 Service Analytics',
            body: [
              'SoAlive may collect technical and operational information necessary to operate, maintain, secure, and improve the Service, including usage statistics, system performance data, error logs, request volume, and account activity.',
              'Such information will not be used to train AI models using your content unless separately consented to by you.',
            ],
          },
        ],
      },
      {
        title: '6. Model Availability and Service Limitations',
        body: [
          'SoAlive provides access to one or more proprietary or third-party AI models. The availability, capabilities, and outputs of the Service may vary depending on the model selected by the user.',
          'Certain models available through SoAlive may be subject to model-specific usage policies, technical limitations, provider restrictions, output filtering or safety controls, content compatibility limitations, rate limits, or usage limits.',
          'The same prompt may produce different results across models. Certain models may decline, fail to generate outputs, be modified, replaced, or removed from the Service from time to time.',
          'SoAlive does not guarantee that every prompt or input will be accepted or successfully processed by every available model.',
        ],
      },
      {
        title: '7. Intellectual Property',
        body: [
          'All rights, title, and interest in and to SoAlive and the Service, including software, source code, APIs, AI orchestration systems, interface design, trademarks, logos, branding, workflows, and documentation remain the exclusive property of Kande Hong Kong Limited or its licensors.',
          'Except as expressly stated in these Terms, no rights are transferred to you.',
        ],
      },
      {
        title: '8. Fees, Credits and Payment',
        body: [
          'Access to certain features of the Service may require payment. SoAlive may offer access to image generation and related features through credits, credit packages, promotional credits, subscription plans, or other usage-based billing methods.',
          'Where credits apply, users may be required to purchase credits to access features, and credits may be deducted when generation requests or other chargeable actions are submitted.',
          'The number of credits required may vary depending on the selected model, generation settings, output resolution, or other applicable parameters.',
          'Unless otherwise stated, purchased credits are non-refundable once purchased or used except where required by applicable law, have no cash value, and are non-transferable between users or accounts.',
          'SoAlive reserves the right to revise pricing, credit requirements, package structure, or billing methods at any time.',
        ],
      },
      {
        title: '9. Suspension and Termination',
        body: ['SoAlive may suspend, restrict, or terminate your access to the Service if:'],
        bullets: [
          'you breach these Terms;',
          'your use violates applicable law;',
          'your use creates legal, operational, or security risk;',
          'fraudulent, abusive, or harmful activity is detected.',
        ],
        closing:
          'You may stop using the Service at any time. Upon termination, your access to the Service may be disabled immediately.',
      },
      {
        title: '10. Disclaimer of Warranties',
        body: [
          'The Service is provided "as is" and "as available." To the maximum extent permitted by law, SoAlive disclaims all warranties, whether express or implied, including merchantability, fitness for a particular purpose, non-infringement, uninterrupted availability, and accuracy or reliability of generated outputs.',
          'AI-generated content may contain inaccuracies, omissions, distortions, unexpected results, or unintended similarities to existing works. Your use of the Service and any Output is at your own risk.',
        ],
      },
      {
        title: '11. Limitation of Liability',
        body: [
          'To the fullest extent permitted by law, Kande Hong Kong Limited shall not be liable for any indirect, incidental, consequential, special, exemplary, or punitive damages, including loss of profits, loss of revenue, business interruption, loss of data, or claims arising from generated Output or its use.',
          "To the maximum extent permitted by law, SoAlive's total liability arising from or relating to the Service shall not exceed the total amount paid by you to SoAlive in the twelve (12) months preceding the claim.",
        ],
      },
      {
        title: '12. Changes to These Terms',
        body: [
          'SoAlive may update or revise these Terms from time to time. Updated versions will be published on the SoAlive website with the revised effective date.',
          'Your continued use of the Service after any update constitutes acceptance of the revised Terms.',
        ],
      },
      {
        title: '13. Privacy',
        body: [
          'Your use of the Service is also subject to the SoAlive Privacy Policy, which explains how personal data is collected, used, stored, and protected.',
          'By using the Service, you acknowledge that you have reviewed and accepted the Privacy Policy.',
        ],
      },
      {
        title: '14. Governing Law',
        body: [
          'These Terms shall be governed by and construed in accordance with the laws of Hong Kong Special Administrative Region, without regard to conflict of law principles.',
          'Any dispute arising out of or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts of Hong Kong.',
        ],
      },
    ],
  },
  privacy: {
    title: 'Privacy Policy',
    effectiveDate: '8 May 2026',
    intro: [
      'Welcome to SoAlive, an AI-powered image generation service operated by Kande Hong Kong Limited.',
      'By accessing or using SoAlive, you acknowledge that you have read and understood this Privacy Policy.',
    ],
    sections: [
      {
        title: '1. Information We Collect',
        body: ['When you use SoAlive, we may collect the following information:'],
        groups: [
          {
            title: 'Account Information',
            bullets: [
              'name or display name',
              'email address',
              'job title, if provided',
              'location, if provided',
              'account preferences',
            ],
          },
          {
            title: 'User Content',
            bullets: [
              'text prompts',
              'uploaded images',
              'generated images',
              'generation settings or instructions',
            ],
          },
          {
            title: 'Payment Information',
            body: [
              'If you purchase credits or paid features, payment is processed by our payment providers. We may receive limited transaction information such as:',
            ],
            bullets: ['purchase amount', 'transaction status', 'billing country or region'],
            closing: 'We do not store full payment card details on our servers.',
          },
          {
            title: 'Technical Information',
            bullets: [
              'IP address',
              'browser or device type',
              'operating system',
              'access logs',
              'usage activity',
              'error and diagnostic information',
            ],
          },
        ],
      },
      {
        title: '2. How We Use Information',
        body: ['We use information to:'],
        bullets: [
          'provide and operate SoAlive',
          'generate and deliver AI-generated images',
          'manage user accounts and credits',
          'process purchases',
          'improve platform performance and reliability',
          'maintain security and prevent abuse',
          'provide support and respond to enquiries',
          'comply with legal obligations',
        ],
      },
      {
        title: '3. Prompts, Uploads and Generated Content',
        body: [
          'Your creative content remains yours.',
          "SoAlive does not use your prompts, uploaded content, or generated outputs to train or improve SoAlive's services or models unless you explicitly choose to allow us to do so.",
          'If optional participation in research, model improvement, or feedback programmes becomes available in the future, we will ask for your consent separately.',
        ],
      },
      {
        title: '4. Sharing of Information',
        body: [
          'We do not sell your personal information.',
          'We may share information only when necessary with trusted service providers that help us operate SoAlive, such as hosting and infrastructure providers, payment processors, analytics and performance providers, and AI model or inference providers required to generate requested content.',
          'We may also disclose information if required by law or where necessary to protect our legal rights, users, or the Service.',
        ],
      },
      {
        title: '5. Data Retention',
        body: ['We keep information only as long as reasonably necessary to:'],
        bullets: [
          'provide the Service',
          'maintain your account',
          'process transactions',
          'comply with legal or regulatory obligations',
          'resolve disputes and enforce our terms',
        ],
      },
      {
        title: '6. Security',
        body: [
          'We take reasonable technical and organisational measures to protect your information from unauthorised access, misuse, loss, or disclosure.',
          'However, no online service can guarantee absolute security.',
        ],
      },
      {
        title: '7. Cookies',
        body: ['SoAlive may use cookies and similar technologies to:'],
        bullets: [
          'keep you signed in',
          'remember preferences',
          'improve website functionality',
          'analyse performance and usage',
        ],
        closing: 'You can manage cookies through your browser settings.',
      },
      {
        title: '8. Your Rights',
        body: [
          'Depending on where you live, you may have rights regarding your personal information, including the right to:',
        ],
        bullets: [
          'request access to your information',
          'request correction of inaccurate information',
          'request deletion of your account or data',
          'withdraw consent where applicable',
        ],
      },
      {
        title: '9. Changes to This Policy',
        body: [
          'We may update this Privacy Policy from time to time. When we make changes, the updated version will be published on this page with a revised effective date.',
        ],
      },
      {
        title: '10. Contact Us',
        body: [
          'If you have any questions about this Privacy Policy or how your information is handled, please contact us at support@soalive.net.',
        ],
      },
    ],
  },
};

const renderBody = (items) =>
  (items || []).map((item) => (
    <p key={item}>{item}</p>
  ));

const renderBullets = (items) =>
  items && items.length ? (
    <ul>
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  ) : null;

const renderGroup = (group) => (
  <div className="legal-page__group" key={group.title}>
    <h3>{group.title}</h3>
    {renderBody(group.body)}
    {renderBullets(group.bullets)}
    {group.closing ? <p>{group.closing}</p> : null}
  </div>
);

const LegalDocument = ({ type, user, history, openAuthView: showAuthView }) => {
  const document = DOCUMENTS[type] || DOCUMENTS.terms;
  const isAuthenticated = Boolean(user && user.id);
  const handlePrimaryAction = () => {
    if (isAuthenticated && history && typeof history.push === 'function') {
      history.push('/ai');
      return;
    }
    showAuthView('login');
  };

  return (
    <div className="legal-page">
      <section className="legal-page__hero">
        <div className="legal-page__topbar">
          <Link to="/" className="legal-page__brand" aria-label="Go to SoAlive home">
            {renderBrand()}
          </Link>
          <div className="legal-page__topbar-actions">
            <Link to="/terms-of-use">Terms</Link>
            <Link to="/privacy-policy">Privacy</Link>
            <button
              type="button"
              className="legal-page__topbar-cta"
              onClick={handlePrimaryAction}
            >
              Generate Now
            </button>
          </div>
        </div>
        <div className="legal-page__hero-copy">
          <h1>{document.title}</h1>
          <span>Effective Date: {document.effectiveDate}</span>
        </div>
      </section>

      <main className="legal-page__content">
        <section className="legal-page__intro">
          {document.intro.map((item) => (
            <p key={item}>{item}</p>
          ))}
        </section>

        <section className="legal-page__sections" aria-label={document.title}>
          {document.sections.map((section) => (
            <article className="legal-page__section" key={section.title}>
              <h2>{section.title}</h2>
              {renderBody(section.body)}
              {renderBullets(section.bullets)}
              {(section.groups || []).map(renderGroup)}
              {section.closing ? <p>{section.closing}</p> : null}
            </article>
          ))}
        </section>
      </main>

      <footer className="legal-page__footer">
        <div>
          <div className="legal-page__footer-brand">{renderBrand()}</div>
          <p>Let's Build Your Next Visual Story.</p>
        </div>
        <small>Copyright © 2026 SOALIVE</small>
        <nav className="legal-page__footer-links" aria-label="Legal pages">
          <Link to="/terms-of-use">Terms of Use</Link>
          <Link to="/privacy-policy">Privacy Policy</Link>
        </nav>
      </footer>
    </div>
  );
};

const mapStateToProps = (state) => ({
  user: state.auth.user,
});

const mapDispatchToProps = {
  openAuthView,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(LegalDocument));
